import React, { useState } from "react";
import { Button, Form, ListGroup, Container, Row, Col, Spinner } from "react-bootstrap";
import ReportsService from "../../../../../../../../../services/reports-service";
import { useParams, useLocation  } from 'react-router-dom';

const ReportDocument = ({ formData, saveData, nextStep }) => {
    const reportsService = new ReportsService
    const { interestPaymentDate, reportHash } = useParams();
    // console.log("loaded doc:", formData)

    const [localDocs, setLocalDocs] = useState({
        pdf1: formData.documents.pdf1 || "",
        pdf2: formData.documents.pdf2 || "",
        otherDocuments: formData.documents.otherDocuments || [],
    });

    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState({});
    const [error, setError] = useState(null);

    const handleFileChange = async (e, key) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        await uploadDocuments(files, key);
    };

    const triggerFileInput = (key) => {
        const fileInput = document.getElementById(key);
        if (fileInput) fileInput.click();
    };

    const uploadDocuments = async (files, key) => {
        setUploading(true);
        setError(null);
        
        try {
            if (key === "pdf1" || key === "pdf2") {
                const file = files[0];
                const reader = new FileReader();
                reader.readAsDataURL(file);
                
                reader.onload = async () => {
                    const base64File = reader.result.split(",")[1]; // Extract base64 data
                    const fileName = key === "pdf1" 
                        ? `Notarized Affidavit of Undertaking/${file.name}` 
                        : `BIR Ruling CTC by BIR/${file.name}`;
                    
                    try {
                        await reportsService.uploadReport(
                            "Tax-Exempt Reports", 
                            reportHash, 
                            "Tax-Exempt Reports", 
                            interestPaymentDate, 
                            fileName, 
                            base64File
                        );
    
                        setLocalDocs((prev) => {
                            const updatedDocs = { ...prev, [key]: file.name };
                            saveData({ documents: updatedDocs }); // Immediately save to formData
                            return updatedDocs;
                        });
                        
                    } catch (err) {
                        setError(`Upload failed: ${err.message}`);
                    } finally {
                        setUploading(false);
                    }
                };
            } else {
                // Handle otherDocuments - Prevent Duplicates
                const newFiles = files.filter(file => !localDocs.otherDocuments.includes(file.name));
                if (newFiles.length === 0) {
                    setError("Duplicate files cannot be uploaded.");
                    setUploading(false);
                    return;
                }
    
                const response = await reportsService.workflowRetrieveOtherDocumentsPresignedUrls(
                    "Tax-Exempt Reports",
                    "Tax-Exempt Reports",
                    reportHash,
                    interestPaymentDate,
                    newFiles.map((file) => file.name)
                );
    
                const presignedUrls = response?.presigned_urls || [];
                for (let i = 0; i < newFiles.length; i++) {
                    const file = newFiles[i];
                    const urlData = presignedUrls.find((u) => u.file_name === file.name);
                    if (!urlData) throw new Error(`No upload URL found for ${file.name}`);
                    
                    await reportsService.uploadRequiredDocument(urlData.url, file);
                }
    
                setLocalDocs((prev) => {
                    const updatedDocs = {
                      ...prev,
                      otherDocuments: [...prev.otherDocuments, ...newFiles.map((f) => f.name)],
                    };
                  
                    // Updated call: directly pass an object
                    saveData({
                      documents: { 
                        ...formData.documents,
                        otherDocuments: updatedDocs.otherDocuments
                      }
                    });
                  
                    return updatedDocs;
                  });
                                
                
            }
        } catch (err) {
            setError(`Upload failed: ${err.message}`);
        } finally {
            setUploading(false);
        }
    };
    
    
    
    const removeFile = async (key, index = null) => {
        let filePath = "";
        
        if (key === "pdf1") {
            filePath = `Notarized Affidavit of Undertaking/${localDocs.pdf1}`;
        } else if (key === "pdf2") {
            filePath = `BIR Ruling CTC by BIR/${localDocs.pdf2}`;
        } else {
            filePath = `Other Documents/${localDocs.otherDocuments[index]}`;
        }
        
        setDeleting((prev) => ({ ...prev, [key + (index !== null ? index : '')]: true }));
        try {
            await reportsService.removeFiles(
                "Tax-Exempt Reports",
                "Tax-Exempt Reports",
                reportHash,
                interestPaymentDate,
                filePath
            );
            
            setLocalDocs((prev) => {
                const updatedDocs = { ...prev };
                if (key === "otherDocuments") {
                    updatedDocs.otherDocuments = prev.otherDocuments.filter((_, i) => i !== index);
                } else {
                    updatedDocs[key] = "";
                }
                // console.log('Updated documents:', updatedDocs);
            
                // Ensure complete replacement instead of merging
                saveData({ documents: { 
                    pdf1: updatedDocs.pdf1, 
                    pdf2: updatedDocs.pdf2, 
                    otherDocuments: [...updatedDocs.otherDocuments] // Ensure a fresh array
                }});
            
                return updatedDocs;
            });
            
        } catch (error) {
            console.error("Failed to remove file:", error);
            setError("Error removing file. Please try again.");
        } finally {
            setDeleting((prev) => ({ ...prev, [key + (index !== null ? index : '')]: false }));
        }
    };
    

    const handleNext = () => {
        const updatedData = {
          documents: { 
            pdf1: localDocs.pdf1, 
            pdf2: localDocs.pdf2, 
            otherDocuments: [...localDocs.otherDocuments] 
          }
        };
        // Pass the updated data into nextStep, so it doesn't call saveData with undefined
        nextStep(false, updatedData);
      };
      
    

    return (
        <>
            <Container className="report-container mb-3">
                {error && <div className="alert alert-danger">{error}</div>}
                <div>
                    <h2>Notarized Affidavit of Undertaking</h2>
                    <Form.Group>
                        <Row className="align-items-center">
                            <Col>
                                <input
                                    id="pdf1"
                                    type="file"
                                    style={{ display: "none" }}
                                    onChange={(e) => handleFileChange(e, "pdf1")}
                                    accept="application/pdf"
                                />
                                <Button onClick={() => triggerFileInput("pdf1")} variant="primary">
                                    Choose File
                                </Button>
                                {localDocs.pdf1 && <span className="ml-2">{localDocs.pdf1}</span>}
                            </Col>
                            {localDocs.pdf1 && (
                                <Col>
                                    <Button variant="danger" size="sm" onClick={() => removeFile("pdf1")}>
                                        Remove
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    </Form.Group>

                    <h2>BIR Ruling CTC by BIR</h2>
                    <Form.Group>
                        <Row className="align-items-center">
                            <Col>
                                <input
                                    id="pdf2"
                                    type="file"
                                    style={{ display: "none" }}
                                    onChange={(e) => handleFileChange(e, "pdf2")}
                                    accept="application/pdf"
                                />
                                <Button onClick={() => triggerFileInput("pdf2")} variant="primary">
                                    Choose File
                                </Button>
                                {localDocs.pdf2 && <span className="ml-2">{localDocs.pdf2}</span>}
                            </Col>
                            {localDocs.pdf2 && (
                                <Col>
                                    <Button variant="danger" size="sm" onClick={() => removeFile("pdf2")}>
                                        Remove
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    </Form.Group>

                    <h2>Other Documents</h2>
                    <Form.Group>
                        <input
                            id="otherDocuments"
                            type="file"
                            style={{ display: "none" }}
                            multiple
                            onChange={(e) => handleFileChange(e, "otherDocuments")}
                            accept="application/pdf"
                        />
                        <Button onClick={() => triggerFileInput("otherDocuments")} variant="primary">
                            Add Other Documents
                        </Button>
                    </Form.Group>

                    {uploading && <Spinner animation="border" role="status" />}
                    
                    <ListGroup>
                        {localDocs.otherDocuments.map((doc, index) => (
                            <ListGroup.Item key={index}>
                                <Row>
                                    <Col className="col-10">{doc}</Col>
                                    <Col className="col-2">
                                    {deleting[`otherDocuments${index}`] ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        <Button variant="danger" size="sm" onClick={() => removeFile("otherDocuments", index)}>
                                            Remove
                                        </Button>
                                    )}
                                </Col>
                                </Row>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                    <small style={{ color: 'red' }}>**Uploaded files should be PDF only.</small>
                </div>
            </Container>

            <Container>
                <Row>
                    <Col className="d-flex justify-content-between mb-3">
                        <Button variant="secondary" onClick={() => { saveData({ documents: { ...formData.documents, ...localDocs } }); nextStep(true); }}>
                            Back
                        </Button>

                        <Button variant="primary" onClick={handleNext} disabled={uploading}>
                            Next
                        </Button>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default ReportDocument;